import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUser, FaEnvelope, FaPhone, FaCheckCircle } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import BookingCalendar from './BookingCalendar';
import BookingForm from './BookingForm';
import StarsBackground from './StarsBackground';
import API_BASE_URL from '../config';
import SEO from './SEO';
import './BookingPage.css';

const BookingPage = () => {
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeStart, setSelectedTimeStart] = useState('');
  const [selectedTimeEnd, setSelectedTimeEnd] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  useEffect(() => {
    fetchAvailableDates();
  }, []);

  const fetchAvailableDates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/bookings/available-dates?count=400`);
      const data = await response.json();
      setAvailableDates(data.dates || []);
    } catch (error) {
      console.error('Error fetching available dates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTimeStart('');
    setSelectedTimeEnd('');
    setBookingSuccess(false);
  };


  const handleBookingSuccess = () => {
    setBookingSuccess(true);
    setSelectedDate(null);
    setSelectedTimeStart('');
    setSelectedTimeEnd('');
    fetchAvailableDates(); // Обновляем доступные даты
  };

  return (
    <>
      <SEO 
        title="Booking"
        description="Book Sergey Levitskiy for your event - Professional DJ services for weddings, corporate events, celebrations, and special occasions. Available worldwide."
        keywords="DJ booking, Event booking, Wedding DJ booking, Corporate event DJ, Sergey Levitskiy booking"
      />
      <div className="booking-page">
      {/* Психоделические эффекты фона */}
      <div className="booking-waves"></div>
      <div className="booking-glow"></div>
      <StarsBackground />
      <motion.div
        className="booking-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {bookingSuccess && (
          <motion.div
            className="booking-success"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FaCheckCircle />
            <div>
              <h3>{t('booking.bookingSent')}</h3>
              <p>{t('booking.bookingSentDesc')}</p>
            </div>
          </motion.div>
        )}

        {/* Calendar Section */}
        <motion.section
          className="booking-calendar-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="section-title">
            <FaCalendarAlt /> {t('booking.selectDate')}
          </h2>
          <BookingCalendar
            availableDates={availableDates}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            loading={loading}
          />
        </motion.section>

        {/* Time Selection */}
        {selectedDate && (
          <motion.section
            className="booking-time-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="section-title">
              <FaClock /> {t('booking.selectTime')}
            </h2>
            <div className="time-selection-container">
              <div className="time-selection-group">
                <label htmlFor="timeStart">
                  <h3>{t('booking.startTime')}</h3>
                </label>
                <input
                  type="time"
                  id="timeStart"
                  value={selectedTimeStart}
                  onChange={(e) => {
                    setSelectedTimeStart(e.target.value);
                    setBookingSuccess(false);
                    // Автоматически устанавливаем время окончания на час позже
                    if (e.target.value) {
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      const nextHour = (hours + 1) % 24;
                      setSelectedTimeEnd(`${nextHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
                    }
                  }}
                  className="time-input"
                  required
                />
              </div>
              <div className="time-selection-group">
                <label htmlFor="timeEnd">
                  <h3>{t('booking.endTime')}</h3>
                </label>
                <input
                  type="time"
                  id="timeEnd"
                  value={selectedTimeEnd}
                  onChange={(e) => {
                    setSelectedTimeEnd(e.target.value);
                    setBookingSuccess(false);
                  }}
                  className="time-input"
                  required
                  min={selectedTimeStart}
                />
              </div>
            </div>
          </motion.section>
        )}

        {/* Booking Form */}
        {selectedDate && selectedTimeStart && selectedTimeEnd && (
          <motion.section
            className="booking-form-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="section-title">
              <FaUser /> {t('booking.fillForm')}
            </h2>
            <BookingForm
              selectedDate={selectedDate}
              selectedTimeStart={selectedTimeStart}
              selectedTimeEnd={selectedTimeEnd}
              onSuccess={handleBookingSuccess}
            />
          </motion.section>
        )}

        {/* Info Section */}
        <motion.section
          className="booking-info-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2>{t('booking.bookingDetails')}</h2>
          <div className="info-grid">
            <div className="info-item">
              <FaClock />
              <div>
                <h3>{t('booking.workingHours')}</h3>
                <p>{t('booking.workingHoursDesc')}</p>
              </div>
            </div>
            <div className="info-item">
              <FaMapMarkerAlt />
              <div>
                <h3>{t('booking.location')}</h3>
                <p>{t('booking.locationDesc')}</p>
                <p className="info-note">{t('booking.locationNote')}</p>
              </div>
            </div>
            <div className="info-item">
              <FaEnvelope />
              <div>
                <h3>{t('booking.confirmation')}</h3>
                <p>{t('booking.confirmationDesc')}</p>
                <p className="info-note">{t('booking.confirmationNote')}</p>
              </div>
            </div>
          </div>
        </motion.section>
      </motion.div>
    </div>
    </>
  );
};

export default BookingPage;
